import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { getLoginUrl } from "@/const";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { 
  BookOpen, Users, ClipboardList, Plus, TrendingUp, Activity, ArrowRight, 
  CheckCircle, XCircle, Clock, User
} from "lucide-react";
import { CSSProperties, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { hasAnyRole } from "@/lib/roles";
import { TenantLogo } from "@/components/TenantLogo";

const STATUS_CONFIG = {
  pending: { label: "Ausstehend", icon: Clock, color: "text-yellow-600", bg: "bg-yellow-100" },
  confirmed: { label: "Bestätigt", icon: CheckCircle, color: "text-green-600", bg: "bg-green-100" },
  cancelled: { label: "Storniert", icon: XCircle, color: "text-red-600", bg: "bg-red-100" },
  waitlist: { label: "Warteliste", icon: Clock, color: "text-blue-600", bg: "bg-blue-100" },
};

export default function AdminDashboard() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <TenantLogo className="h-16 w-auto" />
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-2xl font-semibold tracking-tight text-center text-foreground">
              Admin-Bereich
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Bitte melden Sie sich an, um auf den Admin-Bereich zuzugreifen.
            </p>
          </div>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl(config?.authProvider);
            }}
            size="lg"
            className="w-full bg-primary hover:bg-primary/90"
          >
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  if (!hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-2xl font-semibold tracking-tight text-center text-foreground">
              Zugriff verweigert
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Sie haben keine Berechtigung, auf den Admin-Bereich zuzugreifen.
            </p>
          </div>
          <Link href="/">
            <Button>Zur Startseite</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <AdminDashboardContent setSidebarWidth={setSidebarWidth} />
    </SidebarProvider>
  );
}

type AdminDashboardContentProps = {
  setSidebarWidth: (width: number) => void;
};

function AdminDashboardContent({ setSidebarWidth }: AdminDashboardContentProps) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();

  // Fetch stats
  const { data: courses } = trpc.course.list.useQuery({ includeInactive: true });
  const { data: bookings } = trpc.booking.listAll.useQuery();
  const { data: instructors } = trpc.instructor.list.useQuery();
  const { data: recentActivity } = trpc.activity.getRecentChanges.useQuery({ limit: 15 });

  const activeCourses = courses?.filter(c => c.isActive).length || 0;
  const pendingBookings = bookings?.filter(b => b.status === "pending").length || 0;
  const totalBookings = bookings?.length || 0;
  const activeInstructors = instructors?.length || 0;

  return (
    <>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">
                Übersicht
              </span>
            </div>
          </div>
        )}
        <main className="flex-1 p-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground">
              Willkommen zurück, {user?.name}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Aktive Kurse</CardTitle>
                <BookOpen className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{activeCourses}</div>
                <p className="text-xs text-muted-foreground">
                  von {courses?.length || 0} gesamt
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Neue Anmeldungen</CardTitle>
                <ClipboardList className="h-4 w-4 text-secondary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingBookings}</div>
                <p className="text-xs text-muted-foreground">
                  ausstehend
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Anmeldungen gesamt</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalBookings}</div>
                <p className="text-xs text-muted-foreground">
                  alle Buchungen
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Kursleiter</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{activeInstructors}</div>
                <p className="text-xs text-muted-foreground">
                  aktiv
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions - Compact Horizontal Row */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setLocation("/admin/courses")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 hover:bg-primary/20 text-primary font-medium text-sm transition-colors"
            >
              <Plus className="h-4 w-4" />
              Neuer Kurs
            </button>
            <button
              onClick={() => setLocation("/admin/bookings")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 hover:bg-secondary/20 text-secondary font-medium text-sm transition-colors relative"
            >
              <ClipboardList className="h-4 w-4" />
              Anmeldungen
              {pendingBookings > 0 && (
                <span className="ml-1 h-5 min-w-5 px-1.5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {pendingBookings}
                </span>
              )}
            </button>
            <button
              onClick={() => setLocation("/admin/instructors")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground font-medium text-sm transition-colors"
            >
              <Users className="h-4 w-4" />
              Kursleiter
            </button>
          </div>

          {/* Two Column Layout: Activity Feed + Recent Bookings */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Activity Feed */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-5 w-5 text-primary" />
                  Letzte Aktivitäten
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentActivity && recentActivity.length > 0 ? (
                  <ScrollArea className="h-[350px] pr-4">
                    <div className="space-y-3">
                      {recentActivity.map((activity) => {
                        const fromConfig = STATUS_CONFIG[activity.previousStatus as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;
                        const toConfig = STATUS_CONFIG[activity.newStatus as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;
                        const ToIcon = toConfig.icon;
                        
                        return (
                          <div key={activity.id} className="flex gap-3 pb-3 border-b last:border-0">
                            <div className={`flex-shrink-0 h-8 w-8 rounded-full ${toConfig.bg} flex items-center justify-center`}>
                              <ToIcon className={`h-4 w-4 ${toConfig.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm truncate">
                                  {activity.booking?.participantName || "Unbekannt"}
                                </span>
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Badge variant="outline" className="text-xs px-1.5 py-0">
                                    {fromConfig.label}
                                  </Badge>
                                  <ArrowRight className="h-3 w-3" />
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs px-1.5 py-0 ${toConfig.bg} ${toConfig.color} border-0`}
                                  >
                                    {toConfig.label}
                                  </Badge>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {activity.course?.name || "Unbekannter Kurs"}
                                {(activity as any).block && (
                                  <span className="ml-1 text-primary">
                                    (Block {new Date((activity as any).block.startDate).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })})
                                  </span>
                                )}
                              </p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                <User className="h-3 w-3" />
                                <span>{activity.changedBy?.name || "System"}</span>
                                <span>•</span>
                                <span>
                                  {formatDistanceToNow(new Date(activity.createdAt), { 
                                    addSuffix: true, 
                                    locale: de 
                                  })}
                                </span>
                              </div>
                              {activity.note && (
                                <p className="text-xs text-muted-foreground mt-1 italic">
                                  "{activity.note}"
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Noch keine Aktivitäten</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Bookings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ClipboardList className="h-5 w-5 text-secondary" />
                  Letzte Anmeldungen
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bookings && bookings.length > 0 ? (
                  <ScrollArea className="h-[350px] pr-4">
                    <div className="space-y-3">
                      {bookings.slice(0, 10).map(booking => (
                        <div key={booking.id} className="flex items-center justify-between gap-3 pb-3 border-b last:border-0">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{booking.participantName}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {booking.course?.name || "Unbekannter Kurs"}
                              {(booking as any).block && (
                                <span className="ml-1 text-primary">
                                  (Block {new Date((booking as any).block.startDate).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })})
                                </span>
                              )}
                            </p>
                          </div>
                          <Badge 
                            variant={
                              booking.status === "confirmed" ? "default" :
                              booking.status === "pending" ? "secondary" :
                              booking.status === "waitlist" ? "secondary" : "outline"
                            }
                            className={`shrink-0 ${booking.status === "waitlist" ? "bg-blue-100 text-blue-800 border-blue-200" : ""}`}
                          >
                            {booking.status === "confirmed" ? "Bestätigt" :
                             booking.status === "pending" ? "Ausstehend" :
                             booking.status === "waitlist" ? "Warteliste" : "Storniert"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Noch keine Anmeldungen</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </SidebarInset>
    </>
  );
}
